import type {IsActiveMatchOptions} from '@angular/router';

export enum MainMenuPath {
	ANNOUNCEMENT = 'announcements',
	APPLICATIONS = 'applications',
	ARCHITECTURE = 'architecture',
	TECHNOLOGY = 'technology',
	PRISM = 'prism',
	DASHBOARD = 'dashboard',
	CUSTOM_DASHBOARDS = 'custom-dashboards',
	DATA = 'data',
	DEVICES = 'devices',
	USERS = 'users',
	DIAGRAMS = 'diagrams',
	EXTENSION = 'extension',
	FINANCE = 'finance',
	HOME = 'home',
	INFRASTRUCTURE = 'infrastructure',
	INTERCOM = 'intercom',
	LOGIN = 'login',
	OPERATION = 'operation',
	ORGANIZATION = 'organization',
	SETTINGS = 'settings',
	USAGE = 'usage',
	WORKSTATIONS_DASHBOARD = 'workstations-dashboard'
}

export enum SettingsPath {
	ACCOUNT = 'account',
	MEMBERS = 'members',
	PERMISSIONS = 'permissions',
	ROVERS = 'rovers',
	TENANT = 'tenant',
	AUDIT = 'audit',
	AGENT = 'agent',
	EXPORT = 'export',
	KABEEN_AI = 'kabeen-ai',
	API_KEYS = 'api-keys',
	INTEGRATIONS = 'integrations'
}

export enum LoginPath {
	SIGNUP = 'signup',
	RENEW = 'renew'
}

export enum DiagramPath {
	APP_MATRIX = 'application-matrix',
	APP_FLOWS = 'flows',
	APP_LIFE_CYCLE = 'life-cycle',
	APP_COMPARISON = 'comparison',
	QUADRANT = 'quadrant',
	INFRASTRUCTURE = 'infrastructure',
	CAPACITY = 'capacity',
	SAVED = 'saved'
}

export enum UsersPath {
	/** Legacy URL of the workstation list, redirected to DEVICES_WORKSTATIONS_URL. */
	WORKSTATIONS = 'workstations',
	USERS = 'users'
}

export enum DevicesPath {
	WORKSTATIONS = 'workstations',
	MOBILE_DEVICES = 'mobile-devices'
}

export enum InfrastructurePath {
	SERVERS = 'servers',
	NETWORKS = 'networks',
	ROUTERS = 'routers'
}

export enum ApplicationPath {
	VALIDATED = 'validated',
	TOLERATED = 'tolerated',
	REFUSED = 'refused',
	DISCOVERED = 'discovered'
}

export type AppRouteUrl = `/${MainMenuPath}` | `/${MainMenuPath}/${string}`;

export const ANNOUNCEMENT_URL: AppRouteUrl = `/${MainMenuPath.ANNOUNCEMENT}`;
export const APPLICATIONS_URL: AppRouteUrl = `/${MainMenuPath.APPLICATIONS}`;
export const APPLICATIONS_VALIDATED_URL: AppRouteUrl = `/${MainMenuPath.APPLICATIONS}/${ApplicationPath.VALIDATED}`;
export const APPLICATIONS_TOLERATED_URL: AppRouteUrl = `/${MainMenuPath.APPLICATIONS}/${ApplicationPath.TOLERATED}`;
export const APPLICATIONS_REFUSED_URL: AppRouteUrl = `/${MainMenuPath.APPLICATIONS}/${ApplicationPath.REFUSED}`;
export const APPLICATIONS_DISCOVERED_URL: AppRouteUrl = `/${MainMenuPath.APPLICATIONS}/${ApplicationPath.DISCOVERED}`;
export const ARCHITECTURE_URL: AppRouteUrl = `/${MainMenuPath.ARCHITECTURE}`;
export const TECHNOLOGY_URL: AppRouteUrl = `/${MainMenuPath.TECHNOLOGY}`;
export const DASHBOARD_URL: AppRouteUrl = `/${MainMenuPath.DASHBOARD}`;
export const CUSTOM_DASHBOARDS_URL: AppRouteUrl = `/${MainMenuPath.CUSTOM_DASHBOARDS}`;

export const USERS_URL: AppRouteUrl = `/${MainMenuPath.USERS}`;
export const USERS_USERS_URL: AppRouteUrl = `/${MainMenuPath.USERS}/${UsersPath.USERS}`;

export const DEVICES_URL: AppRouteUrl = `/${MainMenuPath.DEVICES}`;
export const DEVICES_WORKSTATIONS_URL: AppRouteUrl = `/${MainMenuPath.DEVICES}/${DevicesPath.WORKSTATIONS}`;
export const DEVICES_MOBILE_DEVICES_URL: AppRouteUrl = `/${MainMenuPath.DEVICES}/${DevicesPath.MOBILE_DEVICES}`;

/**
 * Shell tabs whose links carry the workspace (`?tenant=`): active on their path alone. With the default options a URL
 * reached without it (a hard refresh, a navigate() that drops it) leaves no tab selected.
 */
export const TAB_PATH_MATCH: IsActiveMatchOptions = {paths: 'subset', queryParams: 'ignored', fragment: 'ignored', matrixParams: 'ignored'};

export const EXTENSION_URL: AppRouteUrl = `/${MainMenuPath.EXTENSION}`;
export const INFRA_SERVERS_URL: AppRouteUrl = `/${MainMenuPath.INFRASTRUCTURE}/${InfrastructurePath.SERVERS}`;
export const DIAGRAMS_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}`;
export const DIAGRAM_SAVED_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.SAVED}`;
export const DIAGRAMS_APP_MATRIX_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.APP_MATRIX}`;
export const DIAGRAMS_APP_MAPPING_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.APP_FLOWS}`;
export const DIAGRAMS_APP_LIFE_CYCLE_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.APP_LIFE_CYCLE}`;
export const DIAGRAMS_APP_COMPARISON_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.APP_COMPARISON}`;
export const DIAGRAMS_QUADRANT_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.QUADRANT}`;
export const DIAGRAMS_INFRASTRUCTURE_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.INFRASTRUCTURE}`;
export const DIAGRAMS_CAPACITY_URL: AppRouteUrl = `/${MainMenuPath.DIAGRAMS}/${DiagramPath.CAPACITY}`;

export const INFRASTRUCTURE_URL: AppRouteUrl = `/${MainMenuPath.INFRASTRUCTURE}`;
export const INFRASTRUCTURE_NETWORKS_URL: AppRouteUrl = `/${MainMenuPath.INFRASTRUCTURE}/${InfrastructurePath.NETWORKS}`;
export const INFRASTRUCTURE_SERVERS_URL: AppRouteUrl = `/${MainMenuPath.INFRASTRUCTURE}/${InfrastructurePath.SERVERS}`;
export const INFRASTRUCTURE_ROUTERS_URL: AppRouteUrl = `/${MainMenuPath.INFRASTRUCTURE}/${InfrastructurePath.ROUTERS}`;

export const DATA_URL: AppRouteUrl = `/${MainMenuPath.DATA}`;
export const FINANCE_URL: AppRouteUrl = `/${MainMenuPath.FINANCE}`;
export const HOME_URL: AppRouteUrl = `/${MainMenuPath.HOME}`;
export const INTERCOM_URL: AppRouteUrl = `/${MainMenuPath.INTERCOM}`;
export const LOGIN_RENEW_URL: AppRouteUrl = `/${MainMenuPath.LOGIN}/${LoginPath.RENEW}`;
export const LOGIN_SIGNUP_URL: AppRouteUrl = `/${MainMenuPath.LOGIN}/${LoginPath.SIGNUP}`;
export const LOGIN_URL: AppRouteUrl = `/${MainMenuPath.LOGIN}`;
export const OPERATION_URL: AppRouteUrl = `/${MainMenuPath.OPERATION}`;
export const ORGANIZATION_URL: AppRouteUrl = `/${MainMenuPath.ORGANIZATION}`;
export const SETTINGS_ACCOUNT_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.ACCOUNT}`;
export const SETTINGS_ROVERS_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.ROVERS}`;
export const SETTINGS_TENANT_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.TENANT}`;
export const SETTINGS_MEMBERS_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.MEMBERS}`;
export const SETTINGS_AGENT_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.AGENT}`;
export const SETTINGS_EXPORT_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.EXPORT}`;
export const SETTINGS_KABEEN_AI_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.KABEEN_AI}`;
export const SETTINGS_API_KEYS_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.API_KEYS}`;
export const SETTINGS_INTEGRATIONS_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}/${SettingsPath.INTEGRATIONS}`;
export const SETTINGS_URL: AppRouteUrl = `/${MainMenuPath.SETTINGS}`;
export const PRISM_URL: AppRouteUrl = `/${MainMenuPath.PRISM}`;
export const USAGE_URL: AppRouteUrl = `/${MainMenuPath.USAGE}`;
export const WORKSTATIONS_DASHBOARD_URL: AppRouteUrl = `/${MainMenuPath.WORKSTATIONS_DASHBOARD}`;
